/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, memo } from 'react'
import { isEqual, sortBy } from 'lodash'

import { useEuiTheme } from '@elastic/eui'

import type { AllocatorVizInstance, RegionId, SliderInstanceType } from '@modules/ui-types'

import { useSliderColorPicker } from '@/lib/sliders/colors'

import toPercentage from '../../lib/toPercentage'
import { resolveDeploymentUrlForAnyCluster } from '../../lib/urlBuilder'
import prettySize from '../../lib/prettySize'
import { getInstanceId, getMemoryCapacity } from '../../lib/allocatorInstanceCapacitiesViz'

import { useCapacityColorPicker } from './capacityVizColors'

import type { FC } from 'react'

import './allocatorsTable-capacityViz.scss'

type Props = {
  regionId: RegionId
  instances: AllocatorVizInstance[]
  usedCapacity: number
  totalCapacity: number
  largestCapacity: number
  hideInstanceKindsHairline?: boolean
  isCritical?: boolean
}

const InstanceCapacitiesViz: FC<Props> = ({
  totalCapacity,
  largestCapacity,
  instances,
  usedCapacity,
  hideInstanceKindsHairline,
  regionId,
  isCritical,
}) => {
  const sortedInstances = sortBy(instances, (instance) => -getMemoryCapacity(instance))
  const widthPercent = toPercentage(totalCapacity / largestCapacity)
  const width = `${widthPercent}%`
  const styles = { width }

  return (
    <div style={styles}>
      <div className='allocatorCapacity'>
        <UsedCapacity
          instances={sortedInstances}
          totalCapacity={totalCapacity}
          regionId={regionId}
          isCritical={isCritical}
        />
        <UnusedCapacity usedCapacity={usedCapacity} totalCapacity={totalCapacity} />
      </div>

      {hideInstanceKindsHairline ? null : (
        <InstanceKinds instances={sortedInstances} totalCapacity={totalCapacity} />
      )}
    </div>
  )
}

const UsedCapacity = ({
  instances,
  totalCapacity,
  regionId,
  isCritical,
}: {
  instances: AllocatorVizInstance[]
  totalCapacity: number
  regionId: RegionId
  isCritical?: boolean
}) => {
  const getCapacityColor = useCapacityColorPicker()
  const { euiTheme } = useEuiTheme()

  return (
    <Fragment>
      {instances.map((instance, index) => {
        const alternate = index % 2 !== 0
        const { clusterId, kind, stackDeploymentId } = instance
        const instanceId = getInstanceId(instance)
        const capacity = getMemoryCapacity(instance)
        const usage = toPercentage(capacity / totalCapacity)
        const displayId = clusterId.slice(0, 6)
        const backgroundColor = isCritical
          ? euiTheme.colors.darkestShade
          : getCapacityColor(capacity, alternate)

        const tooltip = `${prettySize(capacity)} ${kind} (${displayId})`

        // The reason we're not using `<Link>` from `react-router` is that
        // it considerably slowed down unmounting the visualizations.
        return (
          <a
            key={`${clusterId}-${kind}-${instanceId}`}
            href={resolveDeploymentUrlForAnyCluster({
              regionId,
              clusterId,
              sliderInstanceType: kind,
              stackDeploymentId,
            })}
            className='allocatorCapacity-used'
            data-tooltip={tooltip}
            style={{
              width: `${usage}%`,
              backgroundColor,
            }}
          />
        )
      })}
    </Fragment>
  )
}

const UnusedCapacity = ({
  usedCapacity,
  totalCapacity,
}: {
  usedCapacity: number
  totalCapacity: number
}) => {
  if (usedCapacity === totalCapacity) {
    return null
  }

  const unused = 100 - toPercentage(usedCapacity / totalCapacity)
  const unusedSpace = totalCapacity - usedCapacity
  const tooltip = `${prettySize(unusedSpace)} of available capacity`

  return (
    <div
      className='allocatorCapacity-unused allocatorCapacity-unused-color'
      data-tooltip={tooltip}
      style={{
        width: `${unused}%`,
      }}
    />
  )
}

const InstanceKinds = ({
  instances,
  totalCapacity,
}: {
  instances: AllocatorVizInstance[]
  totalCapacity: number
}) => {
  const kinds = [] as Array<{
    kind: SliderInstanceType
    width: number
    start: number
    color?: string
  }>

  const getSliderColor = useSliderColorPicker()

  instances.forEach((instance) => {
    const { kind } = instance
    const width = toPercentage(getMemoryCapacity(instance) / totalCapacity)
    const lastKind = kinds[kinds.length - 1]
    const isSameKind = lastKind && lastKind.kind === kind

    if (isSameKind) {
      lastKind.width += width
      return
    }

    const start = lastKind ? lastKind.start + lastKind.width : 0
    const color = getSliderColor(kind)

    kinds.push({ kind, width, start, color })
  })

  return (
    <svg className='allocatorCapacityKinds'>
      {kinds.map(({ kind, start, width, color }) => (
        <rect
          key={`nodes-${kind}:${start}:${width}`}
          x={`${start}%`}
          width={`${width}%`}
          fill={color}
          className='allocatorCapacityKind'
          rx='1'
          ry='1'
        />
      ))}
    </svg>
  )
}

const arePropsEqual = (prevProps, nextProps) => isEqual(prevProps, nextProps)

export default memo(InstanceCapacitiesViz, arePropsEqual)
